import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { billingApi } from './api';
// PRD §13.4 — el DPA precede a cualquier activación de plan de pago.
import { LegalAcceptanceModal } from '@/features/legal/LegalAcceptanceModal';
import { DPA } from '@/features/legal/documents';
import { legalApi } from '@/features/legal/api';
import { queryKeys } from '@/lib/api/queryKeys';
import { toast } from '@/components/ui/Toast';
import { useAuth } from '@/lib/auth/useAuth';
import { useMediaQuery } from '@/lib/hooks/useBreakpoint';
import { usePrefsStore } from '@/lib/prefs/usePrefsStore';
import { useT } from '@/lib/i18n/useT';
import { useFavoritable } from '@/features/favorites/FavoritableContext';
import type { EcPlan } from '@/lib/api/types';
import './pricing/pricing.css';
import { PRICING_COPY_EN, PRICING_COPY_ES, fill, type CompareCell, type CompareKind, type PricingCopy } from './pricing/pricingCopy';
import { Btn, C, Check, EASE, H2, Input, MONO, SANS, SoonChip, mono, type Mode } from './pricing/ui';
import { PlansBlock, graceOf, recCtaOf, recTotalOf, whyTextOf } from './pricing/PlansBlock';
import {
  ENTERPRISE_CONTACT_EMAIL,
  INITIAL_STATE,
  NEED_IDS,
  PRESETS,
  TIER_ORDER,
  clamp,
  deepFill,
  fmt,
  quote,
  tiersWithCatalog,
  vars,
  type CalcState,
  type DetailKey,
  type MainInputKey,
  type NeedId,
  type PathId,
  type PresetId,
  type QuotePath,
  type TierId,
  handoffUrl,
} from './pricing/pricingModel';
import { savePendingPlan } from './pendingPlan';

/*
 * La página de precios, calcada de la maqueta «Xotski Pricing v4» (19 sep
 * 2026, PRD Pricing v2.3 + delta v1.0). Los números y las reglas están en
 * `pricing/pricingModel.ts`; el texto, en inglés y español, en
 * `pricing/pricingCopy.ts`. Aquí sólo hay disposición y estado.
 *
 * Qué es real y qué no, respecto a la maqueta:
 *   · Los precios base salen del catálogo (`/plans/catalog`); si aún no llega,
 *     los de la maqueta.
 *   · «Empieza gratis» lleva al registro. Los planes de pago van al Checkout
 *     de Stripe (con el DPA antes, PRD §13.4): la pantalla de pago de la
 *     maqueta era una simulación con tarjeta y aquí es el resumen del pedido
 *     y un botón hacia Stripe. Los extras y asientos no se cobran hoy — sólo
 *     el plan — y la pantalla lo dice.
 *   · Enterprise y «lo llevamos contigo» arman un correo a ventas con la
 *     configuración: no hay endpoint de contacto todavía.
 *   · El bloque de planes y calculadora es el mismo que xotski.com enmarca
 *     (build del 29 sep 2026): mismos textos, mismas reglas. Sin campo de
 *     código: ningún código mueve un total y el build lo retiró.
 */

const LOGO = '/brand/xotski-midnight.svg';

type View = 'checkout' | 'free' | 'contact' | 'success' | null;

const ICONS = [
  <svg key="a" viewBox="0 0 24 24" width="19" height="19" fill="none" stroke={C.deep} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M3.5 21 14 3" /><path d="M20.5 21 10 3" /><path d="M15.5 21 12 15l-3.5 6" /><path d="M2 21h20" /></svg>,
  <svg key="b" viewBox="0 0 24 24" width="19" height="19" fill="none" stroke={C.deep} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="m7.5 4.27 9 5.15" /><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /><path d="m3.3 7 8.7 5 8.7-5" /><path d="M12 22V12" /></svg>,
  <svg key="c" viewBox="0 0 24 24" width="19" height="19" fill="none" stroke={C.deep} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><line x1="6" x2="10" y1="11" y2="11" /><line x1="8" x2="8" y1="9" y2="13" /><line x1="15" x2="15.01" y1="12" y2="12" /><line x1="18" x2="18.01" y1="10" y2="10" /><path d="M17.32 5H6.68a4 4 0 0 0-3.978 3.59c-.006.052-.01.101-.017.152C2.604 9.416 2 14.456 2 16a3 3 0 0 0 3 3c1 0 1.5-.5 2-1l1.414-1.414A2 2 0 0 1 9.828 16h4.344a2 2 0 0 1 1.414.586L17 18c.5.5 1 1 2 1a3 3 0 0 0 3-3c0-1.544-.604-6.584-.685-7.258-.007-.05-.011-.1-.017-.151A4 4 0 0 0 17.32 5z" /></svg>,
  <svg key="d" viewBox="0 0 24 24" width="19" height="19" fill="none" stroke={C.deep} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 17 13.5 8.5 8.5 13.5 2 7" /><polyline points="16 17 22 17 22 11" /></svg>,
  <svg key="e" viewBox="0 0 24 24" width="19" height="19" fill="none" stroke={C.deep} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M8 2v4" /><path d="M16 2v4" /><rect width="18" height="18" x="3" y="4" rx="2" /><path d="M3 10h18" /><path d="M8 14h.01" /><path d="M12 14h.01" /><path d="M16 14h.01" /><path d="M8 18h.01" /><path d="M12 18h.01" /><path d="M16 18h.01" /></svg>,
];

export function PricingPage() {
  const navigate = useNavigate();
  const t = useT();
  const { isAuthenticated } = useAuth();
  const language = usePrefsStore((s) => s.language);
  const setLanguage = usePrefsStore((s) => s.setLanguage);
  const rawCopy: PricingCopy = language === 'es' ? PRICING_COPY_ES : PRICING_COPY_EN;
  const dateLocale = language === 'es' ? 'es-ES' : 'en-US';
  useFavoritable('Pricing');

  // ── Catálogo: el precio base de cada nivel manda sobre el de la maqueta ──
  const plansQ = useQuery({ queryKey: queryKeys.plans.all, queryFn: billingApi.listPlans });
  const plans: EcPlan[] = useMemo(() => plansQ.data?.items ?? [], [plansQ.data]);
  const T = useMemo(() => tiersWithCatalog(plans), [plans]);
  // Las cifras de la copia salen de los niveles ya pisados por el catálogo.
  const c = useMemo(() => deepFill(rawCopy, vars(T, language === 'es' ? 'es' : 'en')), [rawCopy, T, language]);
  const planOf = (id: TierId) => plans.find((p) => p.slug === id) ?? null;

  // ── Estado del calculador y de la página ──
  const [calc, setCalc] = useState<CalcState>(INITIAL_STATE);
  const [preset, setPreset] = useState<PresetId | null>(null);
  const [mode, setMode] = useState<Mode>('plans');
  const [showCompare, setShowCompare] = useState(false);
  const [faqOpen, setFaqOpen] = useState(-1);
  const narrow = useMediaQuery('(max-width: 899px)');

  const [view, setView] = useState<View>(null);
  const [checkoutTier, setCheckoutTier] = useState<PathId | null>(null);
  const [contactSource, setContactSource] = useState<'enterprise' | 'service'>('enterprise');
  const [email, setEmail] = useState('');
  const [contact, setContact] = useState({ name: '', email: '', company: '', note: '' });

  const q = useMemo(() => quote(calc, T), [calc, T]);
  const rec = q.paths[q.rec];

  // ── Historial: las pantallas superpuestas son entradas propias ──
  const token = useRef('x' + Math.random().toString(36).slice(2, 9));
  const owns = (st: unknown) =>
    !!st && typeof st === 'object' && (st as { xToken?: string }).xToken === token.current && !!(st as { xView?: string }).xView;
  const pushView = (v: Exclude<View, null>, tier: PathId | null) => {
    setView(v);
    setCheckoutTier(tier);
    try { window.history.pushState({ xToken: token.current, xView: v }, ''); } catch { /* sin historial */ }
    window.scrollTo(0, 0);
  };
  // Las pantallas finales sustituyen su propio paso: volver atrás devuelve a precios, no a un pago.
  const replaceView = (v: Exclude<View, null>) => {
    setView(v);
    try {
      const st = { xToken: token.current, xView: v };
      if (owns(window.history.state)) window.history.replaceState(st, '');
      else window.history.pushState(st, '');
    } catch { /* sin historial */ }
    window.scrollTo(0, 0);
  };
  const close = useCallback(() => {
    setView(null);
    if (owns(window.history.state)) {
      try { window.history.back(); } catch { /* sin historial */ }
    }
  }, []);
  useEffect(() => {
    const onPop = (ev: PopStateEvent) => setView(owns(ev.state) ? ((ev.state as { xView: Exclude<View, null> }).xView) : null);
    const onKey = (ev: KeyboardEvent) => { if (ev.key === 'Escape') close(); };
    window.addEventListener('popstate', onPop);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('keydown', onKey);
    };
  }, [close]);

  // ── Acciones ──
  const num = (k: MainInputKey | DetailKey, v: string | number, min: number, max: number) => {
    const n = typeof v === 'number' ? v : parseInt(v, 10);
    if (Number.isNaN(n)) return;
    setCalc((s) => ({ ...s, [k]: clamp(n, min, max) }));
    setPreset(null);
  };
  const step = (k: MainInputKey, d: number, min: number, max: number) => {
    setCalc((s) => ({ ...s, [k]: clamp(s[k] + d, min, max) }));
    setPreset(null);
  };
  const toggleNeed = (k: NeedId) => {
    setCalc((s) => ({ ...s, needs: { ...s.needs, [k]: !s.needs[k] } }));
    setPreset(null);
  };
  const applyPreset = (id: PresetId) => {
    const ps = PRESETS.find((p) => p.id === id)!;
    setCalc({ ...ps.s, needs: { leadGen: false, liveOps: false, pdf: false, sso: false, ...ps.needs } });
    setPreset(id);
  };

  const go = (tier: PathId) => {
    if (tier === 'enterprise') { setContactSource('enterprise'); pushView('contact', 'enterprise'); }
    else if (tier === 'free' || tier === 'alc') pushView('free', 'free');
    else pushView('checkout', tier);
  };
  const goService = () => { setContactSource('service'); pushView('contact', 'enterprise'); };

  /*
   * Lo que xotski.com manda al entregar al visitante (`register?plan=…&events=…`)
   * y lo que el propio build acepta (`?lang`, `?mode`): la calculadora abre con
   * esa misma configuración, y un plan pedido abre directamente su pantalla.
   */
  const [searchParams] = useSearchParams();
  const handedOff = useRef(false);
  useEffect(() => {
    if (handedOff.current) return;
    handedOff.current = true;
    const sp = searchParams;
    const lang = sp.get('lang');
    if (lang === 'es' || lang === 'en') setLanguage(lang);
    const num = (k: string, min: number, max: number) => {
      const v = parseInt(sp.get(k) ?? '', 10);
      return Number.isFinite(v) ? clamp(v, min, max) : null;
    };
    const patch: Partial<Omit<CalcState, 'needs'>> = {};
    const e = num('events', 1, 120); if (e != null) patch.e = e;
    const x = num('xotskies', 50, 2000); if (x != null) patch.x = x;
    const b = num('brands', 1, 24); if (b != null) patch.b = b;
    const ty = num('types', 1, 20); if (ty != null) patch.t = ty;
    const l = num('languages', 1, 8); if (l != null) patch.l = l;
    const pr = num('projects', 1, 20); if (pr != null) patch.pr = pr;
    const ed = num('editors', 1, 24); if (ed != null) patch.ed = ed;
    const needs = (sp.get('needs') ?? '').split(',').filter((k): k is NeedId => (NEED_IDS as string[]).includes(k));
    if (Object.keys(patch).length || needs.length) {
      setCalc((st) => ({ ...st, ...patch, needs: { ...st.needs, ...Object.fromEntries(needs.map((k) => [k, true])) } }));
      setPreset(null);
    }
    if (sp.get('mode') === 'calc') setMode('calc');
    const plan = sp.get('plan');
    if (plan === 'xk1' || plan === 'xk2' || plan === 'free' || plan === 'alc' || plan === 'enterprise') go(plan);
    // D1: viene de registrarse o de entrar con este plan ya elegido: al pago sin más clics.
    if (sp.get('resume') === '1' && (plan === 'xk1' || plan === 'xk2')) setAutoPay(plan);
    // Sólo al entrar: después manda lo que el visitante toque.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── El pago real: DPA y después el Checkout de Stripe ──
  const checkout = useMutation({
    mutationFn: (planId: number) => billingApi.createCheckout(planId),
    onSuccess: (session) => window.location.assign(session.url),
    onError: (e) => toast({ tone: 'error', title: t('pricing.checkoutFailed'), description: (e as Error).message }),
  });
  const [pendingPlan, setPendingPlan] = useState<EcPlan | null>(null);
  const [dpaModalOpen, setDpaModalOpen] = useState(false);
  const proceedToCheckout = (plan: EcPlan) => {
    if (!plan.stripePriceId) { toast({ tone: 'info', title: t('pricing.talkToUs'), description: c.overlay.checkout.unavailable }); return; }
    checkout.mutate(plan.id);
  };
  const payNow = async () => {
    if (checkout.isPending) return;
    const tier = checkoutTier === 'xk1' || checkoutTier === 'xk2' ? checkoutTier : null;
    if (!isAuthenticated) {
      // El plan y la configuración esperan en la pestaña: al volver de entrar
      // (o de registrarse) se reanuda aquí mismo, en el pago.
      if (tier) savePendingPlan(tier, handoffUrl(tier, calc, language === 'es' ? 'es' : 'en', '').replace(/^\?/, ''));
      navigate('/login');
      return;
    }
    const plan = tier ? planOf(tier) : null;
    if (!plan) { toast({ tone: 'info', title: t('pricing.talkToUs'), description: c.overlay.checkout.unavailable }); return; }
    try {
      const existing = await legalApi.check(DPA.type, DPA.versionHash);
      if (existing) { proceedToCheckout(plan); return; }
    } catch { /* fallo de red: se enseña el DPA; el backend es idempotente */ }
    setPendingPlan(plan);
    setDpaModalOpen(true);
  };

  // D1 · reanudar: con sesión y catálogo, el pago arranca solo (DPA si falta, y Stripe).
  const [autoPay, setAutoPay] = useState<'xk1' | 'xk2' | null>(null);
  const autoPaid = useRef(false);
  useEffect(() => {
    if (!autoPay || autoPaid.current || !isAuthenticated || !plansQ.isFetched || checkoutTier !== autoPay) return;
    autoPaid.current = true;
    void payNow();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- una sola vez, cuando todo está
  }, [autoPay, isAuthenticated, plansQ.isFetched, checkoutTier]);

  const signup = () => {
    if (isAuthenticated) { toast({ tone: 'info', title: c.overlay.free.already }); return; }
    navigate(email.trim() ? `/register?email=${encodeURIComponent(email.trim())}` : '/register');
  };

  const chips = (): string[] => {
    const s = calc;
    return [
      fill(c.chips.events, { n: s.e }),
      fill(s.b === 1 ? c.chips.brand : c.chips.brands, { n: s.b }),
      fill(c.chips.xotskies, { n: s.x }),
      fill(s.pr === 1 ? c.chips.project : c.chips.projects, { n: s.pr }),
      fill(s.ed === 1 ? c.chips.seat : c.chips.seats, { n: s.ed }),
    ].concat(NEED_IDS.filter((n) => s.needs[n]).map((n) => c.calc.needs[n]));
  };
  const sendContact = () => {
    if (!contact.name.trim() || !contact.email.trim() || !contact.company.trim()) {
      toast({ tone: 'info', title: c.overlay.contact.needFields });
      return;
    }
    const kind = contactSource === 'service' ? c.overlay.contact.serviceTitle : c.overlay.contact.enterpriseTitle;
    const subject = fill(c.overlay.contact.mailSubject, { kind, company: contact.company.trim() });
    const body = [
      `${c.overlay.contact.fields.name}: ${contact.name.trim()}`,
      `${c.overlay.contact.fields.email}: ${contact.email.trim()}`,
      `${c.overlay.contact.fields.company}: ${contact.company.trim()}`,
      '',
      chips().join(' · '),
      q.paths.enterprise.total != null ? `${c.calc.lines['base.enterprise']} ${fmt(q.paths.enterprise.total)}+` : '',
      '',
      contact.note.trim(),
    ].join('\n');
    window.location.href = `mailto:${ENTERPRISE_CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    replaceView('success');
  };

  const ck: QuotePath = (checkoutTier && q.paths[checkoutTier]) || rec;
  const gross = ck.total ?? ck.computed ?? null;
  const fmtDate = (d: Date) => d.toLocaleDateString(dateLocale, { month: 'long', day: 'numeric', year: 'numeric' });
  const renew = fmtDate(new Date(Date.now() + 365 * 864e5));
  const hasAddonLine = ck.lines.some((i) => i.id === 'unlimitedAddon');
  const seatLine = ck.lines.find((i) => i.id === 'seatsExtra') ?? null;
  const lineLabel = (id: keyof PricingCopy['calc']['lines']) => c.calc.lines[id];
  const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);
  const whyText = whyTextOf(c, q, T);
  const recCta = recCtaOf(c, q);
  const recGo = () => go(q.rec === 'alc' ? 'free' : q.rec);
  const recTotal = recTotalOf(c, q);
  const showBar = narrow && !view;

  const cellText = (v: CompareCell) => (v === true ? '✓' : v === false ? '−' : String(v));
  const cellColor = (v: CompareCell, kind: CompareKind, soon = false) => (soon ? '#BBBBBB' : v === false ? C.faint : v === true ? C.accent : kind === 'key' ? C.deep : C.text);

  return (
    <div className="xkp" style={{ background: C.white, color: C.text, fontFamily: SANS, fontSize: 16, lineHeight: '24px', minHeight: '100vh', paddingBottom: narrow ? 92 : 0 }}>
      {/* ── Cabecera fija ── */}
      <div style={{ height: 68, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'sticky', top: 0, background: 'rgba(255,255,255,0.94)', backdropFilter: 'blur(8px)', zIndex: 40, borderBottom: `1px solid ${C.hair}` }}>
        <div style={{ width: '100%', maxWidth: 1140, padding: '0 28px', display: 'flex', alignItems: 'center', gap: 28 }}>
          {/* CH-10: el logo es el camino de vuelta a la app, como en el resto del producto. */}
          <Link to="/" aria-label={c.overlay.toApp} style={{ display: 'block', flex: 'none' }}>
            <img src={LOGO} alt="xotski" style={{ height: 21, width: 'auto', display: 'block' }} />
          </Link>
          <div style={{ flex: 1 }} />
          <a href="#detail" style={{ fontSize: 14, color: C.mid }}>{c.nav.detail}</a>
          <Btn onClick={() => go('free')} hover="xkp-hover-lime" style={{ height: 44, padding: '0 20px', borderRadius: 999, background: C.lime, border: `1px solid ${C.lime}`, color: C.ink, fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', transition: `background 150ms ${EASE}` }}>
            {c.nav.startFree}
          </Btn>
        </div>
      </div>

      {/* ── Hero ── */}
      <section style={{ padding: '64px 28px 24px', display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: '100%', maxWidth: 1140 }}>
          <h1 style={{ fontFamily: MONO, fontWeight: 700, fontSize: 'clamp(32px, 3.8vw, 48px)', lineHeight: 1.12, letterSpacing: '-0.03em', margin: 0, maxWidth: 720, textWrap: 'pretty', color: C.ink }}>
            {c.hero.title1} <br />{c.hero.title2}
          </h1>
          <div style={{ display: 'grid', gap: 12, maxWidth: 900, margin: '20px 0 0' }}>
            {c.hero.points.map((p, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '34px 1fr', gap: 14, alignItems: 'start' }}>
                <div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(178,98,245,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>{ICONS[i]}</div>
                <div style={{ fontSize: 17, lineHeight: '26px', color: C.mid, textWrap: 'pretty' }}>
                  <strong style={{ color: C.ink, fontWeight: 700 }}>{p.strong}</strong> {p.rest}
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>


      <PlansBlock
        c={c}
        T={T}
        q={q}
        calc={calc}
        preset={preset}
        mode={mode}
        narrow={narrow}
        onMode={setMode}
        onGo={go}
        onNum={num}
        onStep={step}
        onToggleNeed={toggleNeed}
        onPreset={applyPreset}
      />

      {/* ── Garantías ── */}
      <section style={{ padding: '0 28px 72px', display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: '100%', maxWidth: 1140, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 18 }}>
          {c.assurances.map((a) => (
            <div key={a.title} style={{ borderLeft: `2px solid ${C.lime}`, padding: '2px 0 2px 16px' }}>
              <div style={{ fontSize: 14, lineHeight: '21px', fontWeight: 700, color: C.ink }}>{a.title}</div>
              <div style={{ fontSize: 13, lineHeight: '20px', color: C.dim, marginTop: 3 }}>{a.body}</div>
            </div>
          ))}
        </div>
      </section>

      <div id="detail" />

      {/* ── Todo el detalle ── */}
      <section style={{ padding: '72px 28px', background: C.soft, borderTop: `1px solid ${C.line}`, display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: '100%', maxWidth: 1140 }}>
          <H2>{c.detail.title}</H2>
          <p style={{ fontSize: 16, lineHeight: '25px', color: C.mid, margin: '12px 0 0', maxWidth: 640 }}>{c.detail.intro}</p>

          <Btn onClick={() => setShowCompare((v) => !v)} hover="xkp-hover-border" style={{ marginTop: 22, display: 'inline-flex', alignItems: 'center', gap: 10, padding: '12px 20px', borderRadius: 999, border: `1px solid ${C.line}`, background: C.white, fontSize: 14, fontWeight: 600, color: C.text, transition: `border-color 150ms ${EASE}` }}>
            <span>{showCompare ? c.detail.hide : fill(c.detail.showAll, { n: c.detail.compare.length })}</span>
            <span style={{ fontFamily: MONO, color: C.accent }}>{showCompare ? '−' : '+'}</span>
          </Btn>

          {showCompare ? (
            <div style={{ marginTop: 24, overflowX: 'auto', border: `1px solid ${C.line}`, borderRadius: 16, background: C.white }}>
              <div style={{ minWidth: 940 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '330px repeat(4, 1fr)', borderBottom: `1px solid ${C.line}`, background: C.soft }}>
                  <div style={{ padding: '15px 22px' }} />
                  {TIER_ORDER.map((id, i) => (
                    <div key={id} style={{ padding: '15px 16px', textAlign: 'center', background: i === 2 ? 'rgba(178,98,245,0.05)' : 'transparent' }}>
                      <div style={{ fontFamily: MONO, fontWeight: 700, fontSize: 15, color: C.ink }}>{T[id].name}</div>
                      <div style={{ fontSize: 12, lineHeight: '18px', color: C.dim, fontVariantNumeric: 'tabular-nums' }}>
                        {id === 'free' ? c.detail.headFree : id === 'enterprise' ? c.detail.headEnterprise : fmt(T[id].base) + c.detail.perYearShort}
                      </div>
                    </div>
                  ))}
                </div>
                {c.detail.compare.map((row, i) => (
                  <div key={row.label} style={{ display: 'grid', gridTemplateColumns: '330px repeat(4, 1fr)', borderBottom: '1px solid #F4F4F4', background: i % 2 === 1 ? '#FCFCFC' : C.white }}>
                    <div style={{ padding: '13px 22px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 14, lineHeight: '20px', fontWeight: 600, color: row.soon ? '#999999' : C.text }}>{row.label}</span>
                        {row.soon ? <SoonChip>{c.detail.soon}</SoonChip> : null}
                      </div>
                      <div style={{ fontSize: 12, lineHeight: '18px', color: C.dim }}>{row.desc}</div>
                    </div>
                    {row.cells.map((v, ci) => (
                      <div key={ci} style={{ padding: '13px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', background: ci === 2 ? 'rgba(178,98,245,0.05)' : 'transparent' }}>
                        <span style={{ fontFamily: typeof v === 'number' || row.kind === 'num' || row.kind === 'price' ? MONO : SANS, fontSize: 13, lineHeight: '18px', fontWeight: row.kind === 'key' || row.kind === 'price' ? 700 : 400, color: cellColor(v, row.kind, row.soon), fontVariantNumeric: 'tabular-nums' }}>{cellText(v)}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div style={{ marginTop: 44 }}>
            <h3 style={{ fontFamily: MONO, fontWeight: 700, fontSize: 20, lineHeight: '28px', margin: 0, color: C.ink }}>{c.detail.alc.title}</h3>
            <p style={{ fontSize: 15, lineHeight: '24px', color: C.mid, margin: '10px 0 0', maxWidth: 640 }}>{c.detail.alc.intro}</p>
            <p style={{ fontSize: 13, lineHeight: '20px', color: C.dim, margin: '6px 0 0', maxWidth: 640 }}>{c.detail.alc.note}</p>
            <div style={{ marginTop: 20, border: `1px solid ${C.line}`, borderRadius: 16, background: C.white, overflowX: 'auto' }}>
              <div style={{ minWidth: 780 }}>
                {[...c.detail.alc.rows, c.detail.alc.seatRow].map((a) => (
                  <div key={a.item} style={{ display: 'grid', gridTemplateColumns: '26% 15% 17% 42%', padding: '14px 22px', borderBottom: '1px solid #F4F4F4', alignItems: 'baseline' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 14, lineHeight: '20px', fontWeight: 600, color: a.soon ? '#999999' : C.text }}>{a.item}</span>
                      {a.soon ? <SoonChip>{c.detail.soon}</SoonChip> : null}
                    </div>
                    <div style={{ fontFamily: MONO, fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: C.ink }}>{a.price}</div>
                    <div style={{ fontSize: 13, lineHeight: '20px', color: C.dim }}>{a.unit}</div>
                    <div style={{ fontSize: 13, lineHeight: '20px', color: a.soon ? '#999999' : C.mid }}>{a.note}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ marginTop: 18, background: C.ink, borderRadius: 20, padding: 'clamp(22px, 3.5vw, 32px)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))', gap: '20px 32px', alignItems: 'center' }}>
              <div style={{ display: 'grid', gap: 8 }}>
                <div style={{ fontFamily: MONO, fontSize: 'clamp(22px, 2.6vw, 28px)', lineHeight: 1.1, fontWeight: 700, color: C.white }}>{c.detail.alc.service.title}</div>
                <div style={{ fontSize: 15, lineHeight: '24px', color: C.lilac, maxWidth: 680, textWrap: 'pretty' }}>{c.detail.alc.service.body}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, fontSize: 13, lineHeight: '20px', color: 'rgba(226,223,242,0.7)' }}>
                  <SoonChip dark>{c.detail.soon}</SoonChip>
                  <span>{c.detail.alc.service.atledi}</span>
                </div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
                {c.detail.alc.service.prices.map((pr) => (
                  <div key={pr.name} style={{ display: 'grid', gap: 2, minWidth: 150, padding: '12px 16px', borderRadius: 16, border: '1px solid rgba(169,255,1,0.35)' }}>
                    <div style={{ fontSize: 12, lineHeight: '16px', color: C.lilac }}>{pr.name}</div>
                    <div style={{ fontFamily: MONO, fontSize: 20, lineHeight: '26px', fontWeight: 700, color: '#A9FF01', fontVariantNumeric: 'tabular-nums' }}>{pr.price}</div>
                    <div style={{ fontSize: 12, lineHeight: '16px', color: C.lilac }}>{pr.unit}</div>
                  </div>
                ))}
                <Btn onClick={goService} hover="xkp-hover-lime" style={{ height: 44, padding: '0 20px', borderRadius: 999, background: '#A9FF01', color: C.ink, fontSize: 14, fontWeight: 700, display: 'inline-flex', alignItems: 'center' }}>
                  {c.detail.alc.service.cta}
                </Btn>
              </div>
            </div>
            <div style={{ fontSize: 13, lineHeight: '20px', color: C.dim, marginTop: 14 }}>{c.detail.alc.demoNote}</div>
          </div>
        </div>
      </section>

      {/* ── Roles ── */}
      <section style={{ padding: '72px 28px', display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: '100%', maxWidth: 1140 }}>
          <H2>{c.roles.title}</H2>
          <p style={{ fontSize: 16, lineHeight: '25px', color: C.mid, margin: '12px 0 0', maxWidth: 660 }}>{c.roles.intro}</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 18, marginTop: 32 }}>
            {c.roles.cards.map((r) => {
              const gratis = r.price === c.roles.free;
              return (
                <div key={r.name} style={{ background: C.white, border: `1px solid ${gratis ? C.line : C.accent}`, borderRadius: 16, padding: 22, display: 'grid', gap: 10, alignContent: 'start' }}>
                  <div style={{ fontSize: 16, lineHeight: '22px', fontWeight: 700, color: C.ink }}>{r.name}</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span style={{ fontFamily: MONO, fontSize: 24, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: gratis ? C.deep : C.ink }}>{r.price}</span>
                    <span style={{ fontSize: 12, lineHeight: '16px', color: C.dim }}>{r.per}</span>
                  </div>
                  <div style={{ fontSize: 14, lineHeight: '21px', color: C.mid }}>{r.can}</div>
                  <div style={{ fontSize: 12, lineHeight: '18px', color: C.deep, fontWeight: 600 }}>{r.count}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Por qué construir gratis es seguro ── */}
      <section style={{ padding: '0 28px 72px', display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: '100%', maxWidth: 1140, background: C.soft, borderRadius: 20, padding: 40 }}>
          <H2 style={{ fontSize: 'clamp(20px, 2.2vw, 26px)', lineHeight: 1.25 }}>{c.enforcement.title}</H2>
          <p style={{ fontSize: 15, lineHeight: '24px', color: C.mid, margin: '10px 0 0', maxWidth: 640 }}>{c.enforcement.intro}</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 20, marginTop: 28 }}>
            {c.enforcement.items.map((en) => (
              <div key={en.title} style={{ display: 'grid', gap: 7, alignContent: 'start' }}>
                <div style={{ fontFamily: MONO, fontSize: 14, fontWeight: 700, color: C.accent }}>{en.title}</div>
                <div style={{ fontSize: 14, lineHeight: '21px', color: C.mid }}>{en.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Preguntas ── */}
      <section style={{ padding: '0 28px 88px', display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: '100%', maxWidth: 760 }}>
          <H2 style={{ margin: '0 0 28px' }}>{c.faq.title}</H2>
          <div style={{ display: 'grid', gap: 6 }}>
            {c.faq.items.map((f, i) => (
              <div key={f.q} style={{ borderBottom: `1px solid ${C.hair}` }}>
                <Btn onClick={() => setFaqOpen((o) => (o === i ? -1 : i))} style={{ padding: '16px 4px', display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'center' }}>
                  <div style={{ fontSize: 16, lineHeight: '24px', fontWeight: 600, color: C.text }}>{f.q}</div>
                  <div style={{ fontFamily: MONO, fontSize: 18, color: C.accent }}>{faqOpen === i ? '−' : '+'}</div>
                </Btn>
                {faqOpen === i ? <div style={{ padding: '0 4px 18px', fontSize: 15, lineHeight: '24px', color: C.mid, maxWidth: 640 }}>{f.a}</div> : null}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pie ── */}
      <div style={{ background: C.ink, padding: '36px 28px', display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: '100%', maxWidth: 1140 }}>
          <footer style={{ borderTop: '1px solid rgba(226,223,242,0.12)', padding: '14px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: 'rgba(226,223,242,0.65)', fontFamily: SANS }}>
            <span>{c.footer.partOf}<b style={{ color: '#A9FF01', fontWeight: 700 }}>Onxite</b>{c.footer.ecosystem}</span>
            <span style={{ fontFamily: MONO }}>{c.footer.powered}</span>
          </footer>
          <div style={{ fontFamily: MONO, fontSize: 11, color: 'rgba(226,223,242,0.45)', padding: '8px 24px 0' }}>{c.stamp}</div>
        </div>
      </div>

      {/* ── Barra fija en móvil ── */}
      {showBar ? (
        <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 50, background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(8px)', borderTop: `1px solid ${C.line}`, padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 14, boxShadow: '0 -2px 12px rgba(27,15,43,0.06)' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, lineHeight: '16px', color: C.dim }}>{fill(c.bar.perYear, { name: rec.name })}</div>
            <div style={mono({ fontSize: 22, lineHeight: '28px' })}>{recTotal}</div>
          </div>
          <Btn onClick={recGo} style={{ height: 46, padding: '0 22px', borderRadius: 999, background: C.accent, color: C.white, fontWeight: 600, display: 'flex', alignItems: 'center', flex: 'none' }}>{recCta}</Btn>
        </div>
      ) : null}

      {/* ── Pantallas superpuestas ── */}
      {view ? (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100, background: C.white, overflowY: 'auto' }}>
          <div style={{ height: 68, borderBottom: `1px solid ${C.hair}`, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'sticky', top: 0, background: C.white, zIndex: 2 }}>
            <div style={{ width: '100%', maxWidth: 1000, padding: '0 28px', display: 'flex', alignItems: 'center', gap: 24 }}>
              <Link to="/" aria-label={c.overlay.toApp} style={{ display: 'block', flex: 'none' }}>
                <img src={LOGO} alt="xotski" style={{ height: 20, width: 'auto', display: 'block' }} />
              </Link>
              <div style={{ flex: 1 }} />
              <Btn onClick={close} hover="xkp-hover-accent" style={{ fontSize: 14, color: C.mid }}>{c.overlay.back}</Btn>
            </div>
          </div>

          {view === 'checkout' ? (
            <div style={{ minHeight: '100vh', background: C.white, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))' }}>
              <div style={{ background: '#F6F5F8', borderRight: `1px solid ${C.line}`, padding: '40px 32px 64px', display: 'flex', justifyContent: 'flex-end' }}>
                <div style={{ width: '100%', maxWidth: 400, display: 'grid', gap: 26, alignContent: 'start' }}>
                  <div style={{ fontFamily: MONO, fontWeight: 700, fontSize: 15, letterSpacing: '-0.01em', color: C.ink }}>xotski</div>
                  <div>
                    <div style={{ fontSize: 15, lineHeight: '22px', color: C.mid }}>{fill(c.overlay.checkout.subscribe, { name: ck.name })}</div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 8 }}>
                      <div style={mono({ fontSize: 40, lineHeight: '46px', letterSpacing: '-0.03em' })}>{gross == null ? c.calc.answer.talkToUs : fmt(gross)}</div>
                      <div style={{ fontSize: 14, color: C.dim }}>{c.overlay.checkout.perYear}</div>
                    </div>
                    <div style={{ display: 'grid', gap: 3, marginTop: 10 }}>
                      <div style={{ fontSize: 12, lineHeight: '18px', color: C.dim }}>{fill(c.overlay.checkout.renews, { date: renew })}</div>
                      {hasAddonLine ? <div style={{ fontSize: 12, lineHeight: '18px', color: C.dim }}>{fill(c.overlay.checkout.addonProrated, { date: renew })}</div> : null}
                      {seatLine ? <div style={{ fontSize: 12, lineHeight: '18px', color: C.dim }}>{fill(c.overlay.checkout.seatProrated, { date: renew, amount: fmt(seatLine.amount) })}</div> : null}
                    </div>
                  </div>
                  <div style={{ display: 'grid', gap: 9 }}>
                    {ck.lines.map((li) => (
                      <div key={li.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, fontSize: 14, lineHeight: '21px', color: C.mid }}>
                        <span>{lineLabel(li.id)}</span>
                        <span style={{ fontFamily: MONO, fontVariantNumeric: 'tabular-nums', color: li.included ? C.accent : C.ink }}>{li.included ? c.calc.answer.included : fmt(li.amount)}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'grid', gap: 9, borderTop: `1px solid ${C.line}`, paddingTop: 16 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, fontSize: 14, lineHeight: '21px', color: C.mid }}>
                      <span>{c.overlay.checkout.subtotal}</span>
                      <span style={{ fontFamily: MONO, fontVariantNumeric: 'tabular-nums', color: C.ink }}>{gross == null ? c.calc.answer.talkToUs : fmt(gross)}</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, borderTop: `1px solid ${C.line}`, paddingTop: 14, alignItems: 'baseline' }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>{c.overlay.checkout.totalDue}</span>
                      <span style={mono({ fontSize: 20 })}>{ck.path === 'xk1' || ck.path === 'xk2' ? fmt(T[ck.path].base) : gross == null ? c.calc.answer.talkToUs : fmt(gross)}</span>
                    </div>
                    {ck.lines.length > 1 ? <div style={{ fontSize: 12, lineHeight: '18px', color: C.dim }}>{c.overlay.checkout.addonsNote}</div> : null}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                    {chips().map((yc) => (
                      <div key={yc} style={{ padding: '6px 12px', borderRadius: 999, background: C.white, border: `1px solid ${C.line}`, fontSize: 12, color: C.mid }}>{yc}</div>
                    ))}
                  </div>
                  <div style={{ fontSize: 12, lineHeight: '18px', color: C.dim }}>{fill(c.calc.answer.grace, { pct: graceOf(T, checkoutTier) })}</div>
                </div>
              </div>

              <div style={{ padding: '40px 32px 64px', display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{ width: '100%', maxWidth: 420, display: 'grid', gap: 20, alignContent: 'start' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.mid }}>{c.overlay.checkout.payWith}</div>
                  <Btn onClick={() => void payNow()} hover="xkp-hover-purple" style={{ height: 52, borderRadius: 999, background: checkout.isPending ? C.deep : C.accent, color: C.white, fontWeight: 600, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {checkout.isPending ? c.overlay.checkout.processing : !isAuthenticated ? c.overlay.checkout.signIn : fill(c.overlay.checkout.stripeCta, { amount: ck.path === 'xk1' || ck.path === 'xk2' ? fmt(T[ck.path].base) : '' })}
                  </Btn>
                  <div style={{ fontSize: 12, lineHeight: '18px', color: C.dim, textAlign: 'center' }}>{c.overlay.checkout.stripeNote}</div>
                </div>
              </div>
            </div>
          ) : null}

          {view === 'free' ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '64px 28px 96px' }}>
              <div style={{ width: '100%', maxWidth: 480 }}>
                <h2 style={{ fontFamily: MONO, fontWeight: 700, fontSize: 30, lineHeight: '38px', letterSpacing: '-0.02em', margin: 0, color: C.ink }}>{c.overlay.free.title}</h2>
                <p style={{ fontSize: 16, lineHeight: '24px', color: C.mid, margin: '12px 0 26px' }}>{fill(c.overlay.free.blurb, { n: T.free.events ?? 3 })}</p>
                <div style={{ display: 'grid', gap: 14 }}>
                  <Input type="email" placeholder={c.overlay.free.placeholder} value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') signup(); }} style={{ padding: '15px 16px', fontSize: 16 }} />
                  <Btn onClick={signup} hover="xkp-hover-purple" style={{ height: 54, borderRadius: 999, background: C.accent, color: C.white, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{c.overlay.free.cta}</Btn>
                  <div style={{ fontSize: 13, lineHeight: '20px', color: C.dim }}>{c.overlay.free.note}</div>
                </div>
              </div>
            </div>
          ) : null}

          {view === 'contact' ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 28px 96px' }}>
              <div style={{ width: '100%', maxWidth: 1000, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24, alignItems: 'start' }}>
                <div>
                  <h2 style={{ fontFamily: MONO, fontWeight: 700, fontSize: 30, lineHeight: '38px', letterSpacing: '-0.02em', margin: 0, color: C.ink }}>{contactSource === 'service' ? c.overlay.contact.serviceTitle : c.overlay.contact.enterpriseTitle}</h2>
                  <p style={{ fontSize: 16, lineHeight: '24px', color: C.mid, margin: '12px 0 20px', textWrap: 'pretty' }}>{contactSource === 'service' ? c.overlay.contact.serviceBlurb : c.overlay.contact.enterpriseBlurb}</p>
                  <div style={{ background: C.soft, border: `1px solid ${C.accent}`, borderRadius: 14, padding: 20, display: 'grid', gap: 8 }}>
                    <div style={{ fontFamily: MONO, fontSize: 20, fontWeight: 700, color: C.ink }}>{contactSource === 'service' ? c.overlay.contact.servicePrice : c.overlay.contact.enterprisePrice}</div>
                    <div style={{ fontSize: 13, lineHeight: '20px', color: C.mid }}>{contactSource === 'service' ? c.overlay.contact.serviceReason : whyText ?? c.overlay.contact.enterpriseReasonDefault}</div>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16 }}>
                    {chips().map((yc) => (
                      <div key={yc} style={{ padding: '7px 14px', borderRadius: 999, background: C.soft, border: `1px solid ${C.line}`, fontSize: 13, color: C.mid }}>{yc}</div>
                    ))}
                  </div>
                </div>
                <div style={{ display: 'grid', gap: 14 }}>
                  {(['name', 'email', 'company'] as const).map((f) => (
                    <Input key={f} type={f === 'email' ? 'email' : 'text'} placeholder={c.overlay.contact.fields[f]} value={contact[f]} onChange={(e) => setContact((s) => ({ ...s, [f]: e.target.value }))} />
                  ))}
                  <textarea placeholder={c.overlay.contact.notePlaceholder} value={contact.note} onChange={(e) => setContact((s) => ({ ...s, note: e.target.value }))} rows={3} style={{ width: '100%', boxSizing: 'border-box', padding: '14px 16px', borderRadius: 12, border: `1.5px solid ${C.line}`, background: C.white, color: C.ink, fontSize: 15, resize: 'vertical' }} />
                  <Btn onClick={sendContact} hover="xkp-hover-purple" style={{ height: 54, borderRadius: 999, background: C.accent, color: C.white, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{c.overlay.contact.cta}</Btn>
                </div>
              </div>
            </div>
          ) : null}

          {view === 'success' ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 28px 96px' }}>
              <div style={{ width: '100%', maxWidth: 560 }}>
                <div style={{ width: 44, height: 44, borderRadius: 999, background: C.accent, color: C.white, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 700 }}>✓</div>
                <h2 style={{ fontFamily: MONO, fontWeight: 700, fontSize: 30, lineHeight: '38px', letterSpacing: '-0.02em', margin: '22px 0 0', color: C.ink }}>
                  {checkoutTier === 'free' ? c.overlay.success.freeTitle : checkoutTier === 'enterprise' ? c.overlay.success.enterpriseTitle : fill(c.overlay.success.onTitle, { name: checkoutTier && checkoutTier !== 'alc' ? T[checkoutTier].name : 'Xotski' })}
                </h2>
                <p style={{ fontSize: 17, lineHeight: '27px', color: C.mid, margin: '12px 0 26px', textWrap: 'pretty' }}>{checkoutTier === 'enterprise' ? c.overlay.success.enterpriseBody : c.overlay.success.body}</p>
                <div style={{ display: 'grid', gap: 10 }}>
                  {(checkoutTier === 'enterprise'
                    ? c.overlay.success.stepsEnterprise.map((s) => fill(s, { n: calc.e, estimate: q.paths.enterprise.total != null ? fmt(q.paths.enterprise.total) + '+' : c.overlay.success.custom }))
                    : c.overlay.success.steps.map((s) => fill(s, { n: calc.pr, projectWord: plural(calc.pr, c.calc.summary.project, c.calc.summary.projects) }))
                  ).map((s) => <Check key={s} size={15}>{s}</Check>)}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 26 }}>
                  <Btn onClick={() => navigate('/')} hover="xkp-hover-lime" style={{ height: 50, padding: '0 24px', borderRadius: 999, background: C.lime, border: `1px solid ${C.lime}`, color: C.ink, fontWeight: 700, display: 'inline-flex', alignItems: 'center' }}>{c.overlay.toApp}</Btn>
                  <Btn onClick={close} hover="xkp-hover-border" style={{ height: 50, padding: '0 24px', borderRadius: 999, border: `1px solid ${C.line}`, color: C.text, fontWeight: 600, display: 'inline-flex', alignItems: 'center' }}>{c.overlay.success.back}</Btn>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* PRD §13.4 — DPA antes del Checkout de Stripe. */}
      {dpaModalOpen && (
        <LegalAcceptanceModal
          document={DPA}
          onDone={() => {
            setDpaModalOpen(false);
            if (pendingPlan) { const next = pendingPlan; setPendingPlan(null); proceedToCheckout(next); }
          }}
          onDismiss={() => { setDpaModalOpen(false); setPendingPlan(null); }}
        />
      )}
    </div>
  );
}
